/**
 * App root - providers + routing (port v1 design).
 *
 * Routes zgodne z new-design URL conventions (po polsku):
 *   /           -> HubPage (Studio launcher)
 *   /studio/:toolSlug -> ToolPage (per-tool flow)
 *   /oferty     -> GalleryPage (lista ofert)
 *   /wyceny     -> ValuationsPage (lista zapisanych wycen), /wyceny/:id -> raport
 *   /oferty/:projectId -> ProjectPage (szczegóły oferty)
 *   /oferta/nowa | /oferta/:projectId -> OfferWizardPage (kreator)
 *   /galeria    -> GalleryPhotosPage (galeria zdjęć)
 *   /galeria/zdjecie/:jobId -> JobDetailPage
 *   /marka      -> BrandPage
 *   /agencja    -> AgencyPage
 *   /konto      -> AccountPage
 *   /cennik     -> PricingPage (plany abonamentowe + paczki kredytów)
 *   /cennik-abonament -> przekierowanie na /cennik (adres z okresu, gdy cenniki były dwa)
 *   /auth       -> AuthPage
 *   /handoff    -> HandoffPage (przejęcie sesji ze starej wersji, tokeny w #fragmencie)
 *   /payment-success -> PaymentSuccessPage
 */
import { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/hooks/useAuth";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";
import { AppShell } from "@/components/layout/AppShell";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { BrandMark } from "@/components/ui/BrandMark";
import { ConsentBanner } from "@/components/ConsentBanner";
import { RouteAnalytics } from "@/components/RouteAnalytics";

const AuthPage = lazy(() => import("@/pages/AuthPage").then((m) => ({ default: m.AuthPage })));
const HubPage = lazy(() => import("@/pages/HubPage").then((m) => ({ default: m.HubPage })));
const ToolPage = lazy(() => import("@/pages/ToolPage").then((m) => ({ default: m.ToolPage })));
const GalleryPage = lazy(() => import("@/pages/GalleryPage").then((m) => ({ default: m.GalleryPage })));
const ValuationsPage = lazy(() => import("@/pages/ValuationsPage").then((m) => ({ default: m.ValuationsPage })));
const ValuationDetailPage = lazy(() => import("@/pages/ValuationDetailPage").then((m) => ({ default: m.ValuationDetailPage })));
const GalleryPhotosPage = lazy(() => import("@/pages/GalleryPhotosPage").then((m) => ({ default: m.GalleryPhotosPage })));
const JobDetailPage = lazy(() => import("@/pages/JobDetailPage").then((m) => ({ default: m.JobDetailPage })));
const PricingPage = lazy(() => import("@/pages/PricingPage").then((m) => ({ default: m.PricingPage })));
const AccountPage = lazy(() => import("@/pages/AccountPage").then((m) => ({ default: m.AccountPage })));
const BrandPage = lazy(() => import("@/pages/BrandPage").then((m) => ({ default: m.BrandPage })));
const AgencyPage = lazy(() => import("@/pages/AgencyPage").then((m) => ({ default: m.AgencyPage })));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage").then((m) => ({ default: m.PaymentSuccessPage })));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })));
const AdminPage = lazy(() => import("@/pages/AdminPage").then((m) => ({ default: m.AdminPage })));
const ProjectPage = lazy(() => import("@/pages/ProjectPage").then((m) => ({ default: m.ProjectPage })));
const OfferWizardPage = lazy(() => import("@/pages/OfferWizardPage").then((m) => ({ default: m.OfferWizardPage })));
const OfferDnaPage = lazy(() => import("@/pages/OfferDnaPage").then((m) => ({ default: m.OfferDnaPage })));
const StartPage = lazy(() => import("@/pages/StartPage").then((m) => ({ default: m.StartPage })));
const AcceptInvitationPage = lazy(() => import("@/pages/AcceptInvitationPage").then((m) => ({ default: m.AcceptInvitationPage })));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })));
const HandoffPage = lazy(() => import("@/pages/HandoffPage").then((m) => ({ default: m.HandoffPage })));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage").then((m) => ({ default: m.VerifyEmailPage })));
const FloorPlanLabPage = lazy(() => import("@/pages/lab/FloorPlanLabPage").then((m) => ({ default: m.FloorPlanLabPage })));
const FloorPlanEditorPage = lazy(() => import("@/pages/lab/FloorPlanEditorPage").then((m) => ({ default: m.FloorPlanEditorPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Redirect wsteczny: stary URL projektu /galeria/projekt/:id -> /oferty/:id */
function LegacyProjectRedirect() {
  const { projectId } = useParams();
  return <Navigate to={`/oferty/${projectId ?? ""}`} replace />;
}

function PageFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}

function SplashLoader() {
  return (
    <div className="splash">
      <div className="splash__stage">
        <div className="splash__logo">
          <div className="splash__shadow"><BrandMark size={100} /></div>
          <div className="splash__mark"><BrandMark size={100} /></div>
        </div>
        <div className="splash__word">Adres<b>Flow</b></div>
        <div className="splash__dots"><i></i><i></i><i></i></div>
      </div>
    </div>
  );
}

export function App() {
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setBooting(false), 1750);
    return () => clearTimeout(t);
  }, []);
  return (
    <ChunkErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <RouteAnalytics />
            <Routes>
              <Route path="/auth" element={<Suspense fallback={<PageFallback />}><AuthPage /></Suspense>} />
              {/* Wejście ze starej wersji z sesją w #fragmencie — musi być publiczne,
                  bo w momencie wejścia sesji jeszcze nie ma w tym originie. */}
              <Route path="/handoff" element={<Suspense fallback={<PageFallback />}><HandoffPage /></Suspense>} />
              <Route path="/accept-invitation" element={<Suspense fallback={<PageFallback />}><AcceptInvitationPage /></Suspense>} />
              {/* Publiczne — user wchodzi z linku w mailu, może nie mieć sesji.
                  Ścieżki są narzucone przez nadawców maili: AuthPage ustawia
                  redirectTo=${origin}/reset-password, API buduje ${APP_URL}/verify-email?token= */}
              <Route path="/reset-password" element={<Suspense fallback={<PageFallback />}><ResetPasswordPage /></Suspense>} />
              <Route path="/verify-email" element={<Suspense fallback={<PageFallback />}><VerifyEmailPage /></Suspense>} />
              <Route element={<ProtectedRoute />}>
                <Route path="/start" element={<Suspense fallback={<PageFallback />}><StartPage /></Suspense>} />
                <Route path="/payment-success" element={<Suspense fallback={<PageFallback />}><PaymentSuccessPage /></Suspense>} />
                  <Route element={<AppShell />}>
                    <Route path="/" element={<HubPage />} />
                    <Route path="/studio/:toolSlug" element={<ToolPage />} />
                    <Route path="/oferty" element={<GalleryPage />} />
                    <Route path="/wyceny" element={<ValuationsPage />} />
                    <Route path="/wyceny/:valuationId" element={<ValuationDetailPage />} />
                    <Route path="/oferty/:projectId" element={<ProjectPage />} />
                    <Route path="/galeria" element={<GalleryPhotosPage />} />
                    <Route path="/galeria/zdjecie/:jobId" element={<JobDetailPage />} />
                    {/* Redirecty wsteczne (stare bookmarki) */}
                    <Route path="/galeria/zdjecia" element={<Navigate to="/galeria" replace />} />
                    <Route path="/galeria/projekt/:projectId" element={<LegacyProjectRedirect />} />
                    <Route path="/oferta/dna" element={<OfferDnaPage />} />
                    <Route path="/oferta/nowa" element={<OfferWizardPage />} />
                    <Route path="/oferta/:projectId" element={<OfferWizardPage />} />
                    <Route path="/cennik" element={<PricingPage />} />
                    {/* Adres z okresu dwóch cenników — trzymamy przekierowanie,
                        bo krąży w linkach i zakładkach. */}
                    <Route path="/cennik-abonament" element={<Navigate to="/cennik" replace />} />
                    <Route path="/konto" element={<AccountPage />} />
                    <Route path="/marka" element={<BrandPage />} />
                    <Route path="/agencja" element={<AgencyPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/lab/rzut" element={<FloorPlanLabPage />} />
                    <Route path="/lab/rzut/:projectId" element={<FloorPlanEditorPage />} />
                  </Route>
                </Route>
                <Route path="*" element={<Suspense fallback={<PageFallback />}><NotFoundPage /></Suspense>} />
              </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          <ConsentBanner />
          {booting && <SplashLoader />}
        </AuthProvider>
      </QueryClientProvider>
    </ChunkErrorBoundary>
  );
}
